import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { Link, useLoaderData, useRevalidator } from 'react-router-dom';
import {
  Button,
  cn,
  EmptyState,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@immo/ui-kit';
import { ColumnsTrigger, type ColumnPreference } from '@immo/components';
import type { DeletedDealRow } from '@immo/api-contract';
import { apiClient } from '@/lib/auth/api-client';
import { useTranslations } from '@/i18n/use-translations';
import { ListLeading } from '@/components/layout/page-leading';
import { PageTopbar } from '@/components/layout/topbar-slots';
import { FilterBar } from '@/components/filter-bar';
import { useWorkspaceFilter } from '@/lib/list-view/use-workspace-filter';
import {
  CalendarIcon,
  ClockIcon,
  MembersIcon,
  PropertiesIcon,
  TagIcon,
  WorkspacesIcon,
} from '@/components/layout/icons';
import { deletedBySortValue, useDeletedByLabel } from '@/components/deleted-by';
import { OperatorColumnHead } from '@/components/table/operator-column-head';
import type { OperatorColumn } from '@/components/table/operator-column';
import { loadOperatorColumnPreference, OPERATOR_COLUMN_KEYS } from '@/components/table/operator-column-store';
import { useOperatorTable } from '@/components/table/use-operator-table';

const NOT_DELETED_CODE = 'deal.deal.cannot-restore-not-deleted';
const ACTIVE_DEAL_EXISTS_CODE = 'deal.deal.cannot-restore-active-deal-exists';
const PROPERTY_ARCHIVED_CODE = 'deal.deal.cannot-restore-property-archived';

type RestoreFailure = {
  readonly message: string;
  readonly blockingDealPropertyReference: string | null;
};

export const deletedDealsLoaderParams = [] as const;

export type DeletedDealsLoaderData = {
  readonly items: ReadonlyArray<DeletedDealRow>;
  readonly columnPreference: ColumnPreference;
};

export async function deletedDealsLoader(): Promise<DeletedDealsLoaderData> {
  const [result, columnPreference] = await Promise.all([
    apiClient.adminDeals.listDeleted({ query: {} }),
    loadOperatorColumnPreference(OPERATOR_COLUMN_KEYS.deletedDeals),
  ]);

  if (!result.ok) {
    throw new Response('Failed to load deleted deals', { status: result.status });
  }

  return { items: result.body.items, columnPreference };
}

// At 1280px the kit's icon heads overflow the table's own scroller by 119px, which pushes the restore
// button off-screen (0 at 1440px with labels, 0 at 1280px icon-only), so below 1440px the icon heads
// show only the icon and the head's `title` carries the label. Written as a whole literal so Tailwind
// emits it (BL-043, BL-065; IMM-1074.5 plan).
const LABEL_FROM_1440 = 'hidden min-[1440px]:inline';

function useDeletedDealColumns(): ReadonlyArray<OperatorColumn<DeletedDealRow>> {
  const t = useTranslations('pages.admin.deals.deleted');
  const intl = useIntl();
  const deletedByLabel = useDeletedByLabel();

  return useMemo(
    () => [
      {
        id: 'workspace',
        label: t('columns.workspace'),
        icon: WorkspacesIcon,
        headLabelClassName: LABEL_FROM_1440,
        cellClassName: 'font-semibold text-text-primary',
        cell: (row) => row.workspaceName ?? t('unresolved-workspace'),
        sortValue: (row) => row.workspaceName,
      },
      {
        id: 'object',
        label: t('columns.object'),
        icon: PropertiesIcon,
        headLabelClassName: LABEL_FROM_1440,
        cell: (row) => (
          <div className="min-w-0">
            <div className="truncate text-base font-semibold text-text-primary">
              {row.propertyReference === null ? (
                t('unresolved-property')
              ) : (
                <Link to={`/workspaces/properties/${row.propertyId}`} className="hover:underline">
                  {row.propertyReference}
                </Link>
              )}
            </div>
            {row.propertyDeleted && <div className="text-base-sm text-danger-600">{t('property-deleted')}</div>}
          </div>
        ),
        sortValue: (row) => row.propertyReference,
      },
      {
        id: 'type',
        label: t('columns.type'),
        icon: TagIcon,
        headClassName: 'w-[130px]',
        headLabelClassName: LABEL_FROM_1440,
        cell: (row) => t(`type.${row.type}`),
        sortValue: (row) => t(`type.${row.type}`),
      },
      {
        id: 'status',
        label: t('columns.status'),
        icon: TagIcon,
        headClassName: 'w-[150px]',
        headLabelClassName: LABEL_FROM_1440,
        cell: (row) => t(`status.${row.status}`),
        sortValue: (row) => t(`status.${row.status}`),
      },
      {
        id: 'phase',
        label: t('columns.phase'),
        icon: ClockIcon,
        headClassName: 'w-[190px]',
        headLabelClassName: LABEL_FROM_1440,
        cell: (row) => t(`phase.${row.phase}`),
        sortValue: (row) => t(`phase.${row.phase}`),
      },
      {
        id: 'deletedAt',
        label: t('columns.deleted-at'),
        icon: CalendarIcon,
        headClassName: 'w-[170px]',
        headLabelClassName: LABEL_FROM_1440,
        cellClassName: 'tabular-nums text-text-tertiary',
        cell: (row) => intl.formatDate(row.deletedAt, { dateStyle: 'short', timeStyle: 'short' }),
        sortValue: (row) => Date.parse(row.deletedAt),
      },
      {
        id: 'deletedBy',
        label: t('columns.deleted-by'),
        icon: MembersIcon,
        headClassName: 'w-[180px]',
        headLabelClassName: LABEL_FROM_1440,
        cellClassName: 'max-w-[180px] truncate text-text-secondary',
        cell: (row) => <span title={deletedByLabel(row.deletedBy)}>{deletedByLabel(row.deletedBy)}</span>,
        sortValue: (row) => deletedBySortValue(deletedByLabel(row.deletedBy), row.deletedBy),
      },
    ],
    [t, intl, deletedByLabel],
  );
}

export function DeletedDealsPage() {
  const t = useTranslations('pages.admin.deals.deleted');
  const { items, columnPreference } = useLoaderData() as DeletedDealsLoaderData;
  const revalidator = useRevalidator();

  const [pending, setPending] = useState<DeletedDealRow | null>(null);
  const [restoring, setRestoring] = useState(false);
  const [failure, setFailure] = useState<RestoreFailure | null>(null);

  const filter = useWorkspaceFilter(items);
  const workspaceFilter = filter.workspaceId;

  const filtered = useMemo(
    () => items.filter((item) => workspaceFilter === null || item.workspaceId === workspaceFilter),
    [items, workspaceFilter],
  );

  const catalogue = useDeletedDealColumns();
  const table = useOperatorTable({
    rows: filtered,
    preferenceKey: OPERATOR_COLUMN_KEYS.deletedDeals,
    catalogue,
    alwaysVisibleId: 'object',
    loadedPreference: columnPreference,
  });

  const closeConfirm = () => {
    if (restoring) return;
    setPending(null);
    setFailure(null);
  };

  const failureFor = (status: number, body: unknown): RestoreFailure => {
    const error = (
      body as { error?: { code?: string; details?: { blockingDealPropertyReference?: string | null } } } | undefined
    )?.error;

    if (error?.code === ACTIVE_DEAL_EXISTS_CODE) {
      return {
        message: t('errors.cannot-restore-active-deal-exists'),
        blockingDealPropertyReference: error.details?.blockingDealPropertyReference ?? null,
      };
    }

    if (error?.code === NOT_DELETED_CODE) {
      revalidator.revalidate();

      return { message: t('errors.cannot-restore-not-deleted'), blockingDealPropertyReference: null };
    }

    if (error?.code === PROPERTY_ARCHIVED_CODE) {
      return { message: t('errors.cannot-restore-property-archived'), blockingDealPropertyReference: null };
    }

    return {
      message: status === 403 ? t('errors.forbidden') : t('errors.generic'),
      blockingDealPropertyReference: null,
    };
  };

  const confirmRestore = async (row: DeletedDealRow) => {
    setRestoring(true);
    setFailure(null);

    try {
      const result = await apiClient.adminDeals.restore({ pathParams: { id: row.id } });

      if (result.ok) {
        setPending(null);
        revalidator.revalidate();

        return;
      }

      setFailure(failureFor(result.status, result.body));
    } catch {
      setFailure({ message: t('errors.generic'), blockingDealPropertyReference: null });
    } finally {
      setRestoring(false);
    }
  };

  return (
    <>
      <PageTopbar leading={<ListLeading title={t('title')} count={filtered.length} icon={PropertiesIcon} />} />
      <div className="flex h-full min-h-0 flex-col">
        <FilterBar
          pills={filter.pills ?? undefined}
          {...(filter.pills !== null ? { overflow: filter.overflow } : {})}
          trailing={<ColumnsTrigger {...table.columns.trigger} />}
        />
        {filtered.length === 0 ? (
          <div className="p-6">
            <EmptyState title={t('empty')} />
          </div>
        ) : (
          <div className="min-h-0 flex-1 overflow-auto" data-testid="deleted-deals-table-scroll">
            <Table flush>
              <TableHeader>
                <TableRow {...table.columns.headerRowProps}>
                  {table.columns.visibleColumns.map((column) => (
                    <OperatorColumnHead key={column.id} column={column} table={table} />
                  ))}
                  <TableHead className="w-[170px]">{t('columns.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {table.sorted.map((row) => (
                  <TableRow key={row.id}>
                    {table.columns.visibleColumns.map((column) => (
                      <TableCell
                        key={column.id}
                        className={cn(column.cellClassName, table.columns.bodyCellClassName(column.id))}
                        style={column.cellStyle}
                      >
                        {column.cell(row)}
                      </TableCell>
                    ))}
                    <TableCell>
                      <Button
                        type="button"
                        variant="secondary"
                        disabled={restoring}
                        aria-label={
                          row.propertyReference === null
                            ? t('restore-aria-unresolved')
                            : t('restore-aria', { reference: row.propertyReference })
                        }
                        onClick={() => setPending(row)}
                      >
                        {t('restore')}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
      {pending !== null && (
        <Modal
          open
          onClose={closeConfirm}
          confirmOnClose={false}
          size="md"
          title={t('confirm.title')}
          footer={
            <>
              <Button variant="ghost" type="button" onClick={closeConfirm} disabled={restoring}>
                {t('confirm.cancel')}
              </Button>
              <Button type="button" onClick={() => confirmRestore(pending)} disabled={restoring}>
                {restoring ? t('confirm.loading') : t('confirm.cta')}
              </Button>
            </>
          }
        >
          <p className="break-words text-sm text-text-secondary">
            {t('confirm.body', { workspace: pending.workspaceName ?? t('unresolved-workspace') })}
          </p>
          {pending.propertyDeleted && (
            <p className="mt-3 break-words text-sm text-text-secondary">{t('confirm.body-property-deleted')}</p>
          )}
          {failure !== null && (
            <div className="mt-3 space-y-1 text-sm text-danger-600" role="alert">
              <p>{failure.message}</p>
              {failure.blockingDealPropertyReference !== null && (
                <p>{t('errors.blocking-deal', { reference: failure.blockingDealPropertyReference })}</p>
              )}
            </div>
          )}
        </Modal>
      )}
    </>
  );
}
